/*
 * OpenAI ads pixel: page views, product views, shopper add-to-carts and
 * checkout starts, plus the click and browser references copied onto the cart
 * for the server-side order_created event.
 *
 * Adds and checkouts are observed on fetch rather than hooked at their call
 * sites, which live in legacy JC code. Two theme flows make that subtle:
 * - Checkout, plan changes, cart-prefill links and subscription edit mode all
 *   POST /cart/clear then re-add the whole cart. Adds inside that rebuild are
 *   not shopper adds (assets/cart.js:146, src/templates/components/g-cart.js:291,
 *   bundle-page.js:160 and :1858, sham-cart-prefill.js:236).
 * - Navigation to /checkout happens only after that rebuild, and only once the
 *   delivery-date and minimum-order gates pass. A checkout click followed by a
 *   clear is therefore the checkout start.
 * snippets/initial-storage.liquid wraps fetch too and returns a promise that
 * never settles for an aborted request, so nothing here may wait on one.
 */
(function () {
  'use strict';

  var SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
  // A rebuild's requests are chained within microtasks of each other, so the
  // window stays open while one is in flight and only briefly after.
  var REBUILD_IN_FLIGHT_MS = 30000;
  var REBUILD_TAIL_MS = 750;
  var CHECKOUT_CLICK_WINDOW_MS = 30000;
  var COOKIE_POLL_MS = 250;
  var COOKIE_POLL_LIMIT = 20;
  var CHECKOUT_SELECTOR = '#checkout, #cart-checkout';
  // Set by assets/cart.js:87 and g-cart.js setCheckoutBusy only once the
  // delivery-date and minimum-order gates have passed.
  var CHECKOUT_BUSY_SELECTOR = '[data-loading-bundle="true"]';
  var REF_RE = /^[\w.-]{1,256}$/;
  // Shopify can drop one of two overlapping cart writes, so the attribute
  // write waits for the cart to go quiet. An aborted request may never settle
  // (snippets/initial-storage.liquid), hence the cut-off.
  var CART_QUIET_MS = 1500;
  var CART_WRITE_DEAD_MS = 10000;
  var WRITE_RETRY_MS = 500;

  if (window.__shamOpenaiPixel) return;
  var configEl = document.getElementById('sham-openai-pixel-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  var debug = new URLSearchParams(window.location.search).get('oaiq_debug') === '1';
  var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
  if (role !== 'main' && !debug) return;
  if (window.Shopify && window.Shopify.designMode) return;
  // Edit-mode URLs carry Loop's bearer token; the SDK reports the page URL.
  if (isEditMode()) return;
  window.__shamOpenaiPixel = true;

  var currency = config.currency || 'SGD';
  var nativeFetch = typeof window.fetch === 'function' ? window.fetch : null;
  var rebuildUntil = 0;
  var checkoutClickAt = 0;
  var checkoutSnapshot = null;
  var checkoutFired = false;
  var pendingAttributes = null;
  var cartWrites = [];
  var lastCartWriteAt = 0;
  var writeTimer = null;

  loadSdk();
  window.oaiq('init', debug ? { pixelId: config.pixelId, debug: true } : { pixelId: config.pixelId });

  sendPageView();

  if (config.product) {
    var price = toInt(config.product.price);
    measure('contents_viewed', {
      type: 'contents',
      amount: price,
      currency: currency,
      contents: [
        {
          id: String(config.product.variantId),
          name: config.product.title,
          content_type: 'product',
          quantity: 1,
          amount: price,
          currency: currency,
        },
      ],
    });
  }

  if (nativeFetch) {
    window.fetch = function (input, init) {
      var pending = nativeFetch.apply(this, arguments);
      try {
        observe(input, init, pending);
      } catch (e) {}
      return pending;
    };
  }

  document.addEventListener('click', onClick, true);
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    checkoutFired = false;
    checkoutClickAt = 0;
    sendPageView();
  });
  syncCartAttributes(0);

  function sendPageView() {
    measure('page_viewed', {
      type: 'contents',
      contents: [{ id: window.location.pathname, name: document.title, content_type: 'page' }],
    });
  }

  function loadSdk() {
    if (window.oaiq) return;
    var q = function () {
      q.q.push(arguments);
    };
    q.q = [];
    window.oaiq = q;
    var js = document.createElement('script');
    js.async = true;
    js.src = SDK_URL;
    var first = document.getElementsByTagName('script')[0];
    first.parentNode.insertBefore(js, first);
  }

  function measure(name, data) {
    try {
      window.oaiq('measure', name, data);
    } catch (e) {}
  }

  // Same test as snippets/initial-storage.liquid, whose window flag is set
  // after this async script may already have run.
  function isEditMode() {
    var params = new URLSearchParams(window.location.search);
    return !!window.subscriptionEditModeEnabled || (params.has('transactionId') && params.has('token'));
  }

  function toInt(value) {
    var n = Math.round(Number(value));
    return isFinite(n) ? n : 0;
  }

  function pathOf(url) {
    try {
      return new URL(url, window.location.href).pathname;
    } catch (e) {
      return '';
    }
  }

  function observe(input, init, pending) {
    var url = typeof input === 'string' ? input : input && input.url ? input.url : String(input);
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST') return;
    var path = pathOf(url);
    if (/\/cart\/(add|change|update|clear)(\.js)?$/.test(path)) trackCartWrite(pending);

    if (/\/cart\/clear(\.js)?$/.test(path)) {
      trackRebuildRequest(pending);
      if (
        !checkoutFired &&
        checkoutClickAt &&
        Date.now() - checkoutClickAt < CHECKOUT_CLICK_WINDOW_MS &&
        document.querySelector(CHECKOUT_BUSY_SELECTOR) &&
        !isEditMode()
      ) {
        checkoutFired = true;
        measure('checkout_started', checkoutSnapshot || { type: 'contents', currency: currency });
      }
      return;
    }

    if (!/\/cart\/add(\.js)?$/.test(path)) return;
    if (isEditMode()) return;
    // sham-cart-prefill.js is deferred and may clear the cart before this
    // async script has wrapped fetch.
    if (Date.now() < rebuildUntil || document.querySelector('sham-cart-prefill')) {
      trackRebuildRequest(pending);
      return;
    }

    var requested = requestedQuantities(init && init.body);
    pending
      .then(function (response) {
        if (!response || !response.ok) return;
        return response
          .clone()
          .json()
          .then(function (data) {
            reportAdd(requested, data);
            if (pendingAttributes) writeAttributes();
          });
      })
      .catch(function () {});
  }

  function trackCartWrite(pending) {
    var entry = { at: Date.now() };
    cartWrites.push(entry);
    lastCartWriteAt = entry.at;
    var done = function () {
      cartWrites = cartWrites.filter(function (e) {
        return e !== entry;
      });
      lastCartWriteAt = Date.now();
    };
    pending.then(done, done);
  }

  function cartBusy() {
    var now = Date.now();
    cartWrites = cartWrites.filter(function (e) {
      return now - e.at < CART_WRITE_DEAD_MS;
    });
    return cartWrites.length > 0 || now - lastCartWriteAt < CART_QUIET_MS || now < rebuildUntil;
  }

  function trackRebuildRequest(pending) {
    rebuildUntil = Date.now() + REBUILD_IN_FLIGHT_MS;
    var close = function () {
      rebuildUntil = Date.now() + REBUILD_TAIL_MS;
    };
    pending.then(close, close);
  }

  // The add response gives each line's quantity after the add, so the added
  // quantity comes from the request. Bodies are JSON {items:[{id,quantity}]}
  // or FormData with id/quantity (assets/product-form.js).
  function requestedQuantities(body) {
    var out = {};
    try {
      if (typeof body === 'string') {
        var parsed = JSON.parse(body);
        var items = Array.isArray(parsed.items) ? parsed.items : parsed.id ? [parsed] : [];
        items.forEach(function (item) {
          addTo(out, item.id, item.quantity);
        });
      } else if (body && typeof body.get === 'function') {
        addTo(out, body.get('id'), body.get('quantity'));
      }
    } catch (e) {}
    return out;
  }

  function addTo(out, id, quantity) {
    if (id === undefined || id === null || id === '') return;
    var key = String(id);
    out[key] = (out[key] || 0) + (toInt(quantity) || 1);
  }

  function reportAdd(requested, data) {
    var lines = data && Array.isArray(data.items) ? data.items : data && data.variant_id ? [data] : [];
    var seen = {};
    var contents = [];
    var total = 0;
    lines.forEach(function (line) {
      var key = String(line.variant_id);
      if (seen[key]) return;
      seen[key] = true;
      var quantity = requested[key] || 1;
      var unit = toInt(line.final_price !== undefined ? line.final_price : line.price);
      total += unit * quantity;
      contents.push({
        id: key,
        name: line.product_title || line.title,
        content_type: 'product',
        quantity: quantity,
        amount: unit,
        currency: currency,
      });
    });
    if (!contents.length) return;
    measure('items_added', { type: 'contents', amount: total, currency: currency, contents: contents });
  }

  function onClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function' || !target.closest(CHECKOUT_SELECTOR)) return;
    checkoutClickAt = Date.now();
    if (window.cart) checkoutSnapshot = cartToEvent(window.cart);
    snapshotCart();
  }

  function cartToEvent(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var cartCurrency = cart.currency || currency;
    return {
      type: 'contents',
      amount: toInt(cart.total_price),
      currency: cartCurrency,
      contents: cart.items.map(function (line) {
        return {
          id: String(line.variant_id),
          name: line.product_title || line.title,
          content_type: 'product',
          quantity: toInt(line.quantity),
          amount: toInt(line.final_price),
          currency: cartCurrency,
        };
      }),
    };
  }

  // Taken at the click: by the time the rebuild's clear fires, the cart is
  // about to be emptied.
  function snapshotCart() {
    if (!nativeFetch) return;
    nativeFetch
      .call(window, '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        checkoutSnapshot = cartToEvent(cart) || checkoutSnapshot;
      })
      .catch(function () {});
  }

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return match[1];
    }
  }

  function validRef(value) {
    return value && REF_RE.test(value) ? value : '';
  }

  // The SDK writes __obref once it has loaded, so poll briefly for it. An
  // empty cart is left alone, since writing to it creates a cart for every
  // visitor; the write then follows the first shopper add.
  function syncCartAttributes(attempt) {
    var obref = validRef(readCookie('__obref'));
    if (!obref && attempt < COOKIE_POLL_LIMIT) {
      setTimeout(function () {
        syncCartAttributes(attempt + 1);
      }, COOKIE_POLL_MS);
      return;
    }
    var oppref =
      validRef(readCookie('__oppref')) || validRef(new URLSearchParams(window.location.search).get('oppref'));

    var current = config.cartAttributes || {};
    var attributes = {};
    if (oppref && oppref !== current.oppref) attributes._oppref = oppref;
    if (obref && obref !== current.obref) attributes._obref = obref;
    if (!Object.keys(attributes).length) return;
    pendingAttributes = attributes;
    if (config.itemCount > 0) writeAttributes();
  }

  function scheduleWrite() {
    if (writeTimer) return;
    writeTimer = setTimeout(function () {
      writeTimer = null;
      writeAttributes();
    }, WRITE_RETRY_MS);
  }

  function writeAttributes() {
    if (!nativeFetch || !pendingAttributes) return;
    if (cartBusy()) {
      scheduleWrite();
      return;
    }
    var attributes = pendingAttributes;
    pendingAttributes = null;
    var startedAt = Date.now();
    nativeFetch
      .call(window, '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
      })
      .then(function (response) {
        return response.text();
      })
      .then(function () {
        if (lastCartWriteAt >= startedAt) return recheck(attributes);
      })
      .catch(function () {});
  }

  function recheck(attributes) {
    return nativeFetch
      .call(window, '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var held = (cart && cart.attributes) || {};
        var lost = Object.keys(attributes).some(function (key) {
          return held[key] !== attributes[key];
        });
        if (!lost) return;
        pendingAttributes = Object.assign({}, attributes, pendingAttributes || {});
        scheduleWrite();
      });
  }
})();
